import React, { useEffect, useState } from "react";
import { Typography, IconButton, Collapse } from "@mhome/ui";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BoolInput from "../../input/bool-input";
import TextInput from "../../input/text-input";
import NumberInput from "../../input/number-input";
import DefaultAutomationDetail1Widget from "./default-automation-detail-1";
import { useWebSocket } from "../../ws/websocket";
import { getAutomationStatus } from "../../service/automation-service";
import { prepareWidget } from "../../common/widget-util";

const DefaultDetail1Widget = ({ widget, onValueUpdate }) => {
  const [automationEnabled, setAutomationEnabled] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const { sendAndWaitWs } = useWebSocket();

  useEffect(() => {
    console.debug("default widget", widget);

    // Initialize values from widget.items (filter for global: true)
    if (widget.items) {
      const initialValues = {};
      Object.values(widget.items).forEach((item) => {
        if (item.global === true) {
          initialValues[item.name] =
            item.value || getDefaultValue(item.dataType);
        }
      });
    }

    checkAutomationStatus();
  }, [widget]);

  const checkAutomationStatus = async () => {
    const baseAutomationId = widget.id.includes("|")
      ? `${widget.id.split("|")[1]}`
      : `${widget.id}`;
    const transformedAutomationId = widget.id.startsWith("Q-")
      ? `S-${baseAutomationId}`
      : `P-${baseAutomationId}`;
    try {
      const statusResponse = await getAutomationStatus(
        sendAndWaitWs,
        transformedAutomationId
      );
      console.debug("statusResponse", statusResponse);
      if (statusResponse && !statusResponse.error) {
        setAutomationEnabled(statusResponse.enabled);
      } else {
        setAutomationEnabled(false);
      }
    } catch (error) {
      setAutomationEnabled(false);
    }
  };

  const getDefaultValue = (dataType) => {
    switch (dataType?.toLowerCase()) {
      case "bool":
      case "boolean":
        return false;
      case "number":
        return 0;
      case "string":
      default:
        return "";
    }
  };

  const handleAutomationStatusChange = async () => {
    await checkAutomationStatus();
  };

  const handleDescriptionToggle = (itemKey) => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [itemKey]: !prev[itemKey],
    }));
  };

  const renderInput = (item) => {
    prepareWidget(item);
    const valueSpec = { ...item };

    const onChange = (value) =>
      onValueUpdate({ ...item, value: value.toString() });

    switch (item.dataType?.toLowerCase()) {
      case "bool":
        return <BoolInput valueSpec={valueSpec} onChange={onChange} />;
      case "number":
        return (
          <NumberInput
            valueSpec={valueSpec}
            onChange={onChange}
            onError={() => {}}
          />
        );
      case "string":
      default:
        return (
          <TextInput
            valueSpec={valueSpec}
            onChange={onChange}
            style="compact"
          />
        );
    }
  };

  return (
    <div className="w-full flex flex-col gap-4">
      {/* Settings Controls Section */}
      <div
        className="flex flex-col rounded-2xl"
        style={{
          backgroundColor: "hsl(var(--card))",
        }}
      >
        {/* Embed automation component if manual is true */}
        {widget.manual && (
          <div>
            <DefaultAutomationDetail1Widget
              widget={widget}
              onStatusChange={handleAutomationStatusChange}
            />
          </div>
        )}

        {/* Show settings when automation is enabled */}
        {widget.items &&
          Object.keys(widget.items).length > 0 &&
          automationEnabled && (
            <div className="flex flex-col gap-3 px-4 pt-2 pb-3">
              {Object.values(widget.items)
                .filter((item) => item.global === true)
                .map((item, index) => {
                  const itemKey = `${item.name || "item"}-${index}`;
                  return (
                    <div key={itemKey}>
                      {/* Main row with label and input */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-1">
                          <Typography
                            variant="body2"
                            className="font-medium"
                            style={{
                              color: "hsl(var(--foreground))",
                            }}
                          >
                            {item.displayName || item.name}
                          </Typography>
                          {item.description && (
                            <IconButton
                              className="p-1"
                              onClick={() => handleDescriptionToggle(itemKey)}
                            >
                              <InfoOutlinedIcon
                                className="text-base transition-colors duration-200"
                                style={{
                                  color: expandedDescriptions[itemKey]
                                    ? "hsl(var(--primary))"
                                    : "hsl(var(--muted-foreground))",
                                }}
                              />
                            </IconButton>
                          )}
                        </div>
                        <div className="flex items-center flex-shrink-0">
                          {renderInput(item)}
                        </div>
                      </div>

                      {/* Collapsible description row */}
                      {item.description && (
                        <Collapse in={expandedDescriptions[itemKey] || false}>
                          <div
                            className="mt-2 ml-4 p-3 rounded border-l-4"
                            style={{
                              backgroundColor: "hsl(var(--accent))",
                              borderLeftColor: "hsl(var(--primary))",
                            }}
                          >
                            <Typography
                              variant="body2"
                              className="leading-relaxed"
                              style={{
                                color: "hsl(var(--muted-foreground))",
                              }}
                            >
                              {item.description}
                            </Typography>
                          </div>
                        </Collapse>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
      </div>
    </div>
  );
};

export default DefaultDetail1Widget;
